import type { LocaleCatalog, MessageSchema } from '../locale';

/**
 * ============================================================================
 * @altkey/i18n - Catalogue Français (fr)
 * ============================================================================
 * Implémente intégralement le `MessageSchema` défini dans `../locale.ts`.
 * Toute clé manquante ou supplémentaire par rapport au schéma provoque une
 * erreur de compilation TypeScript : cela garantit que le catalogue français
 * reste synchronisé à 100 % avec les 4 autres langues prises en charge.
 * ============================================================================
 */

const messages: MessageSchema = {
  common: {
    appName: 'AltKey',
    ok: 'OK',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    loading: 'Chargement…',
    retry: 'Réessayer',
    close: 'Fermer',
    copy: 'Copier',
    copied: 'Copié dans le presse-papiers',
  },
  pairing: {
    title: 'Associez vos appareils',
    scanQrPrompt:
      "Scannez le code QR avec votre smartphone pour l'associer à cet ordinateur",
    enterCodeManually: 'Ou saisissez manuellement le code à 6 chiffres',
    waitingForDesktop: "En attente de la connexion de l'ordinateur…",
    waitingForMobile: 'En attente de la connexion du smartphone…',
    connected: 'Appareil connecté',
    disconnected: 'Appareil déconnecté',
    expired: "La session d'association a expiré. Générez un nouveau code.",
    invalidCode: 'Code invalide. Vérifiez et réessayez.',
    keysExchanged: 'Échange des clés de sécurité terminé',
    scanDevice: 'Scanner un appareil',
    showMyCode: 'Afficher mon code',
    hostingPrompt: 'Scannez ce code avec l’autre téléphone',
    goBack: 'Torna Indietro',
    disconnectAction: 'Scollega',
    deviceConnected: '🟢 Dispositivo Connesso',
    backToCategories: '← Torna alle Categorie',
  },
  license: {
    proUnlockedPromo:
      'AltKey est en bêta gratuite mondiale : toutes les fonctionnalités Pro sont débloquées',
    proUnlockedLicense: 'Fonctionnalités Pro débloquées avec votre licence',
    freeTierPrompt: 'Débloquez les fonctionnalités Pro avec une licence AltKey',
    invalidLicense: "La clé de licence saisie n'est pas valide",
    enterLicenseKey: 'Saisissez votre clé de licence',
  },
  search: {
    placeholder: 'Recherchez un symbole, un caractère ou un mot-clé…',
    noResults: 'Aucun résultat trouvé',
    resultsCount: '{count} résultats trouvés',
    filterByCategory: 'Filtrer par catégorie',
    recentSymbols: 'Symboles récemment utilisés',
  },
  tofuDetector: {
    title: 'Détecteur de Tofu',
    scanning: 'Recherche des caractères manquants…',
    issuesFound: '{count} caractères non affichables (tofu) détectés',
    noIssuesFound: 'Aucun caractère manquant détecté : tout est en ordre',
    replaceSuggestion: 'Remplacer par un caractère alternatif compatible',
  },
  macroGenerator: {
    title: 'Générateur de macros',
    recordMacro: 'Enregistrer une nouvelle macro',
    stopRecording: "Arrêter l'enregistrement",
    saveMacro: 'Enregistrer la macro',
    macroSaved: 'Macro enregistrée avec succès',
    deleteMacro: 'Supprimer la macro',
  },
  aiCanvasInput: {
    title: 'Saisie Canvas par IA',
    drawPrompt: 'Dessinez le symbole recherché avec votre doigt ou un stylet',
    recognizing: 'Reconnaissance du dessin en cours…',
    recognitionFailed: 'Impossible de reconnaître le symbole. Essayez de le redessiner.',
    insertSymbol: 'Insérer le symbole',
  },
  settings: {
    title: 'Paramètres',
    language: 'Langue',
    theme: 'Thème',
    themeLight: 'Clair',
    themeDark: 'Sombre',
    themeSystem: 'Suivre le système',
    startOnBoot: "Démarrer au démarrage de l'ordinateur",
    notifications: 'Notifications',
    about: 'À propos',
    version: 'Version {version}',
    checkForUpdates: 'Vérifier les mises à jour',
    signOut: 'Se déconnecter',
  },
  home: {
    title: 'Accueil',
    greeting: 'Bonjour, {name}',
    pairWithDesktop: "Associer à l'ordinateur",
    recentActivity: 'Activité récente',
    quickActions: 'Actions rapides',
    noRecentActivity: 'Aucune activité récente à afficher',
  },
  errors: {
    generic: "Une erreur inattendue s'est produite. Veuillez réessayer.",
    network: 'Erreur de connexion. Vérifiez votre réseau et réessayez.',
    sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
    unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
    notFound: 'Ressource introuvable.',
  },
};

const catalog: LocaleCatalog = {
  locale: 'fr',
  messages,
};

export default catalog;